import { useState } from 'react';
import { Box, Divider, Flex, Image, Text } from '@chakra-ui/react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../../../store/redux/hooks';
import {
  AppleEnhancedDesignFeaturedActions,
  AppleEnhancedDesignImages,
  FeaturedActionSlotKey,
  FieldsState,
  QrFeature,
  TemplateType,
} from '../../../../../store/types/template';
import { useImageBrightness } from '../../hooks/useImageBrightness';
import { mapFieldsForPoster } from '../../utils/posterFieldMapping';
import { FEATURED_ACTION_ASSETS } from './featuredActionAssets';
import { FEATURED_ACTION_SLOT_KEYS } from '../../utils/featuredActionTypes';
import { PosterAdditionalInfoSection } from './PosterAdditionalInfoSection';
import { PosterActionCard } from './PosterActionCard';
import { HTTPS_URL_REGEX } from '../../validation/validationSchema';
import BarcodePreview from '../BarcodePreview';
import { ImagePlaceholder } from '../shared/ImagePlaceholder';
import {
  POSTER_CARD_BG,
  POSTER_CARD_RADIUS,
  POSTER_FACE_ASPECT_RATIO,
  POSTER_INFO_BAR_BG_DARK,
  POSTER_INFO_BAR_BG_LIGHT,
  POSTER_INFO_BAR_BLUR,
  POSTER_INFO_BAR_HEIGHT,
} from './posterCardStyles';

const CARD_ASPECT_RATIO = 2.82;
const CARD_GAP = '6px';
const STACK_GAP = '12px';
const ADDITIONAL_INFO_TITLE = 'Additional Pass Info';
const LOGO_WIDTH = '34%';
const LOGO_MAX_HEIGHT = '9%';
const HEADER_RIGHT_INSET = '18px';
const BARCODE_FOOTER_OVERLAP = '1%';
const BARCODE_BOTTOM = `calc(${POSTER_INFO_BAR_HEIGHT} - ${BARCODE_FOOTER_OVERLAP})`;

const additionalInfoSummary = (fields: { label: string }[]) => {
  const [first, second] = fields.map((field) => field.label.toUpperCase());
  return fields.length === 2
    ? `${first} and ${second}`
    : `${first}, ${second} and More`;
};

const toTitleCase = (label: string) =>
  label
    .toLowerCase()
    .replace(/(^|\s)(\S)/g, (_, space, letter) => space + letter.toUpperCase());

const FieldColumn = ({
  label,
  value,
  color,
  align,
}: {
  label: string;
  value: string;
  color: string;
  align: 'right' | 'center';
}) => (
  <Box textAlign={align}>
    <Text
      fontSize="10px"
      color={color}
      fontWeight="500"
      lineHeight="1.2"
      letterSpacing="-0.2px"
    >
      {label}
    </Text>
    <Text
      fontSize="11px"
      color={color}
      fontWeight="600"
      lineHeight="1.2"
      letterSpacing="-0.2px"
      mt="2px"
    >
      {value}
    </Text>
  </Box>
);

const FeaturedActionCard = ({
  slot,
  type,
  url,
}: {
  slot: FeaturedActionSlotKey;
  type: keyof typeof FEATURED_ACTION_ASSETS;
  url: string;
}) => {
  const isLink = HTTPS_URL_REGEX.test(url);
  const linkProps = isLink
    ? {
        as: 'a' as const,
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {};

  return (
    <Box
      {...linkProps}
      data-testid={`featured-action-card-${slot}`}
      position="relative"
      flex={1}
      minW={0}
      bg="white"
      borderRadius="14px"
      overflow="hidden"
      display="block"
      cursor={isLink ? 'pointer' : 'default'}
      transition="transform 0.15s ease"
      _hover={isLink ? { transform: 'scale(1.02)' } : undefined}
      _active={isLink ? { transform: 'scale(0.98)' } : undefined}
    >
      <Image
        src={FEATURED_ACTION_ASSETS[type]}
        alt=""
        position="absolute"
        inset="0"
        w="100%"
        h="100%"
        objectFit="contain"
        objectPosition="left center"
      />
    </Box>
  );
};

export interface PosterGenericInput {
  templateType: TemplateType;
  fields: FieldsState;
  images: AppleEnhancedDesignImages;
  artworkBrightness?: 'light' | 'dark' | null;
  featuredActions: AppleEnhancedDesignFeaturedActions;
  barcode: QrFeature;
  logo?: string | null;
}

interface ApplePosterGenericPreviewProps {
  input?: PosterGenericInput;
  faceOnly?: boolean;
}

const ApplePosterGenericPreview = ({
  input,
  faceOnly = false,
}: ApplePosterGenericPreviewProps) => {
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const storeInput = useAppSelector(
    (s): PosterGenericInput => ({
      templateType: s.template.templateType,
      fields: s.template.fields,
      images: s.template.appleEnhancedDesign.images,
      artworkBrightness: s.template.appleEnhancedDesign.artworkBrightness,
      featuredActions: s.template.appleEnhancedDesign.featuredActions,
      barcode: s.template.barcode,
      logo: s.template.images.apple.logo || s.template.images.shared.logo,
    }),
    shallowEqual
  );
  const {
    templateType,
    fields,
    images,
    artworkBrightness: storedBrightness,
    featuredActions,
    barcode,
    logo: appleLogo,
  } = input ?? storeInput;

  const artwork = images.artwork;
  const liveBrightness = useImageBrightness(storedBrightness ? null : artwork);
  const brightness = storedBrightness ?? liveBrightness;
  const isLightArtwork = brightness === 'light';
  const textColor = isLightArtwork ? 'gray.900' : 'white';

  const { face, additionalInfoFields } = mapFieldsForPoster({
    templateType,
    fields,
  });
  const headerField = face.header[0];
  const primaryFields = face.primary;

  const filledActionSlots = FEATURED_ACTION_SLOT_KEYS.filter(
    (slot) => featuredActions[slot].type !== null
  );

  if (infoPanelOpen) {
    return (
      <PosterAdditionalInfoSection
        title={ADDITIONAL_INFO_TITLE}
        rows={additionalInfoFields.map((field) => ({
          label: field.label,
          value: field.defaultValue,
        }))}
        onBack={() => setInfoPanelOpen(false)}
      />
    );
  }

  const infoBarBg = isLightArtwork
    ? POSTER_INFO_BAR_BG_LIGHT
    : POSTER_INFO_BAR_BG_DARK;

  const faceCard = (
    <Box
      position="relative"
      w="100%"
      aspectRatio={POSTER_FACE_ASPECT_RATIO}
      flexShrink={0}
      bg={POSTER_CARD_BG}
      borderRadius={POSTER_CARD_RADIUS}
      overflow="hidden"
    >
      {artwork ? (
        <Image
          data-testid="poster-artwork"
          src={artwork}
          alt=""
          position="absolute"
          inset="0"
          w="100%"
          h="100%"
          objectFit="cover"
        />
      ) : (
        <ImagePlaceholder position="absolute" inset="0" w="100%" h="100%" />
      )}

      {appleLogo && (
        <Box position="absolute" top="14px" left="14px" zIndex={1}>
          <Image
            src={appleLogo}
            alt=""
            w={LOGO_WIDTH}
            maxH={LOGO_MAX_HEIGHT}
            objectFit="contain"
            objectPosition="left"
          />
        </Box>
      )}

      {headerField && (
        <Box
          position="absolute"
          top="14px"
          right={HEADER_RIGHT_INSET}
          zIndex={1}
        >
          <FieldColumn
            label={headerField.label.toUpperCase()}
            value={headerField.defaultValue}
            color={textColor}
            align="right"
          />
        </Box>
      )}

      {barcode.enabled && barcode.type && (
        <Flex
          position="absolute"
          left="0"
          right="0"
          bottom={BARCODE_BOTTOM}
          justify="center"
          zIndex={2}
        >
          <Box bg="white" borderRadius="10px" p={1} w="fit-content">
            <BarcodePreview type={barcode.type} value={barcode.value} />
          </Box>
        </Flex>
      )}

      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        h={POSTER_INFO_BAR_HEIGHT}
        borderBottomRadius={POSTER_CARD_RADIUS}
        overflow="hidden"
        zIndex={1}
      >
        <Box
          position="absolute"
          inset="0"
          bg={infoBarBg}
          borderBottomRadius={POSTER_CARD_RADIUS}
          sx={{
            backdropFilter: POSTER_INFO_BAR_BLUR,
            WebkitBackdropFilter: POSTER_INFO_BAR_BLUR,
          }}
        />

        {primaryFields.length > 0 && (
          <Flex
            position="absolute"
            left="14px"
            right="14px"
            top="50%"
            transform="translateY(-50%)"
            justify="space-between"
            align="center"
          >
            {primaryFields.map((field, index) => (
              <FieldColumn
                key={`${field.id}-${index}`}
                label={toTitleCase(field.label)}
                value={field.defaultValue}
                color={textColor}
                align="center"
              />
            ))}
          </Flex>
        )}
      </Box>
    </Box>
  );

  if (faceOnly) return faceCard;

  return (
    <>
      <Flex
        position="absolute"
        inset="18% 8% 4% 7%"
        direction="column"
        gap={STACK_GAP}
      >
        {faceCard}

        {additionalInfoFields.length === 1 && (
          <Box h="44px" flexShrink={0}>
            <PosterActionCard
              label={additionalInfoFields[0].label.toUpperCase()}
              subtitle={additionalInfoFields[0].defaultValue}
              center
              hideIcon
            />
          </Box>
        )}

        {additionalInfoFields.length > 1 && (
          <Box h="44px" flexShrink={0}>
            <PosterActionCard
              label={ADDITIONAL_INFO_TITLE}
              subtitle={additionalInfoSummary(additionalInfoFields)}
              onClick={() => setInfoPanelOpen(true)}
              center
              hideIcon
              chevron
            />
          </Box>
        )}

        {filledActionSlots.length > 0 && (
          <Flex
            w="100%"
            aspectRatio={CARD_ASPECT_RATIO}
            flexShrink={0}
            gap={CARD_GAP}
          >
            {filledActionSlots.map((slot) => (
              <FeaturedActionCard
                key={slot}
                slot={slot}
                type={
                  featuredActions[slot]
                    .type as keyof typeof FEATURED_ACTION_ASSETS
                }
                url={featuredActions[slot].url}
              />
            ))}
          </Flex>
        )}
      </Flex>
      <Flex
        position="absolute"
        inset="90% 10% auto 0"
        my={3}
        width="full"
        justifyContent="center"
        direction="row"
        align="center"
      >
        <Divider
          orientation="vertical"
          borderColor="black"
          w="25%"
          borderWidth="2px"
          borderRadius="25px"
        />
      </Flex>
    </>
  );
};

export default ApplePosterGenericPreview;
